import { Previous } from '@europrocurement/flexy-components';
import {
    CustomOptionResolvers,
    FlexyForm,
    jsonFormToFormStructure,
    visibilityMatch,
} from '@europrocurement/flexy-form';
import { jsonForm, visibilityType } from '@europrocurement/flexy-form/utils/types/formTypes';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { allExpanded, darkStyles, JsonView } from 'react-json-view-lite';
import { Box, type SxProps } from '@mui/system';

import { Button } from '@mui/material';

import { REALM_INTERN, UseKeycloakService } from '@europrocurement/l2d-keycloak';
// eslint-disable-next-line import/no-cycle
import { customItemRenderer } from './customItems/customItemRenderer';

/**
 * Prends en argument un vrai json de l'api
 * données de base ( a mettre dans un useState, et changer l'etat a chaque page )
 *
 * gerrer un mode debug
 * qui affiche le json de la données dans un JsonView
 *
 * exposer son etat
 *  - page courante
 *  - nombre de page
 *  - etat saisie
 *
 * onChange( {
 *      currentPage
 *      pageLength
 *      data
 * } )
 * RG : a chaque changement de page
 *  ou changement de structure
 *  appel onChange avec les 3 objets
 *
 *
 * gerrer un state pour la data
 *
 * mettre la structure de la page courante dans un useMemo
 *
 *
 *
 *
 *
 */

export const visibilityTranslation: Record<visibilityType, string> = {
    FULL: 'Complète',
    PARTIAL: 'Partielle',
    BACK: 'Complète back-office',
};

export type JsonFormProps = {
    json: jsonForm;
    debugMode: boolean;
    // mécanique de champs a cacher ou pas (premium/ ou pas)
    visibility?: visibilityType;
    // reprise a partir d'une page spécifique
    startPage: number;
    // reprise avec une partie des données saisies
    defaultValues: Record<string, unknown>;
    customOptionResolvers?: CustomOptionResolvers;
    onChange?: (currentPage: number, pageLength: number, data?: Record<string, unknown>) => void;
    submitLabel?: string;
    submitStyle: React.CSSProperties;
    submitSx?: SxProps;
    onSkip?: (data?: Record<string, unknown>) => void | Promise<void>;
};

const JsonForm: React.FunctionComponent<JsonFormProps> = function ({
    json,
    debugMode,
    onChange,
    startPage = 0,
    defaultValues = {},
    customOptionResolvers = {},
    visibility = 'FULL',
    submitLabel = 'Continuer',
    submitStyle = {},
    submitSx = {},
    onSkip = undefined,
}: JsonFormProps) {
    const [data, setData] = useState({ ...defaultValues });

    /**
     * a chaque submit il faut sauvegarder les données dans data
     */
    const formContext = useForm({
        mode: 'onTouched',
        defaultValues,
    });

    const copyJson = function (jsonToCopy: jsonForm) {
        return { ...jsonToCopy };
    };

    const kc = UseKeycloakService();

    const jsonFiltered = React.useMemo(() => {
        // setCurrentPage(startPage);
        const jsonCopied = copyJson(json);
        if (jsonCopied.pages) {
            jsonCopied.pages = jsonCopied.pages.filter((page) =>
                visibilityMatch(visibility, page?.visibility || []),
            );
        }
        return jsonCopied;
    }, [json, visibility]);

    const onPrevious = () => {
        // appeler le on change
        if (onChange) {
            onChange(startPage - 1, jsonFiltered.pages.length);
        }
    };

    const onSubmitPage = (submitedData: Record<string, unknown>) => {
        const newData = { ...data, ...submitedData };
        // sauvegarder la donnée dans le state data
        setData(newData);
        // passer a la page suivante
        if (startPage < jsonFiltered.pages.length - 1) {
            const jsonFormElement = document.getElementById('json_form');
            if (jsonFormElement) {
                jsonFormElement.scrollIntoView();
            }
            // appeler le on change
            if (onChange) {
                onChange(startPage + 1, jsonFiltered.pages.length, newData);
            }
        } else if (onChange) {
            // passage étape suivante hors formulaire
            onChange(startPage + 1, jsonFiltered.pages.length, newData);
        }
    };

    const structure = React.useMemo(
        () => jsonFormToFormStructure(jsonFiltered, startPage, visibility, customItemRenderer),
        [jsonFiltered, startPage, visibility],
    );

    if (!jsonFiltered.pages) return null;

    return (
        <div id="json_form">
            {startPage !== 0 ? (
                <Previous
                    onClick={onPrevious}
                    hidden={false}
                />
            ) : null}
            <FlexyForm
                formContext={formContext}
                formStructure={structure}
                formObject={defaultValues}
                onSubmit={onSubmitPage}
                customOptionResolvers={customOptionResolvers}
                submitButton={{
                    render: () => (
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="flex-end"
                            gap={2}
                        >
                            {onSkip !== undefined &&
                            (kc.checkRole(REALM_INTERN) || visibility === 'FULL') ? (
                                <Button
                                    type="submit"
                                    variant="contained"
                                    style={{
                                        marginTop: '2rem',
                                        marginBottom: '16px',
                                        ...submitStyle,
                                    }}
                                    sx={{ ...submitSx }}
                                    onClick={() => onSkip(formContext.getValues())}
                                >
                                    Passer à l&apos;étape suivante
                                </Button>
                            ) : null}
                            <Button
                                type="submit"
                                variant="contained"
                                style={{
                                    marginTop: '2rem',
                                    marginBottom: '16px',
                                    ...submitStyle,
                                }}
                                sx={{ ...submitSx }}
                            >
                                {submitLabel}
                            </Button>
                        </Box>
                    ),
                }}
            />
            {debugMode ? (
                <div>
                    page {startPage + 1} sur {jsonFiltered.pages.length}
                    <JsonView
                        data={data}
                        shouldExpandNode={allExpanded}
                        style={darkStyles}
                    />
                </div>
            ) : null}
        </div>
    );
};

export default JsonForm;
